import { useEffect, useState } from "react";
import { Box, Typography, Paper, Stack, Radio, RadioGroup, FormControlLabel, Button, Alert, CircularProgress } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RichEditor from "../../../../components/rich-editor";
import { generateButtonSx } from "../../../../utils/elementor-style-override";
import useAgentPreferenceCatalog from "./hooks/useAgentPreferenceCatalog";
import useSubmitAgentPreferences from "./hooks/useSubmitAgentPreferences";

const primaryBlue = "#1D54A2";

const saveButtonSx = {
	backgroundColor: `${primaryBlue} !important`,
	color: "#FFFFFF !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#16407F !important", text: "#FFFFFF" }, { bg: "#16407F !important", text: "#FFFFFF" }),
};

const EMPTY_REMARKS = "<p></p>";

const AgentPreferenceChecklist = ({ showSnackbar }) => {
	const { catalog, loading: catalogLoading } = useAgentPreferenceCatalog();
	const { submission, loading: submissionLoading, submitting, submit } = useSubmitAgentPreferences();

	// Only preferences the admin has left available show up here.
	const availableOptions = catalog.filter((option) => option.enabled);

	const [checkedIds, setCheckedIds] = useState([]);
	const [remarks, setRemarks] = useState(EMPTY_REMARKS);
	const [hydrated, setHydrated] = useState(false);

	// Seed local form state from the agent's existing submission once both the
	// catalog and the submission have finished loading.
	useEffect(() => {
		if (hydrated || catalogLoading || submissionLoading) return;

		if (submission) {
			const answeredIds = (submission.answers || []).filter((answer) => answer.answer).map((answer) => answer.catalog_id);
			setCheckedIds(answeredIds);
			setRemarks(submission.remarks || EMPTY_REMARKS);
		}

		setHydrated(true);
	}, [hydrated, catalogLoading, submissionLoading, submission]);

	const loading = catalogLoading || submissionLoading;

	const toggleOption = (id, isYes) => {
		setCheckedIds((prev) => {
			const withoutId = prev.filter((existingId) => existingId !== id);
			return isYes ? [...withoutId, id] : withoutId;
		});
	};

	const handleSave = async () => {
		const answers = availableOptions.map((option) => ({
			catalog_id: option.id,
			answer: checkedIds.includes(option.id),
		}));

		const result = await submit(answers, remarks);

		if (result.success) {
			showSnackbar?.("Your preferences were submitted and are pending admin approval.", "success");
		} else {
			showSnackbar?.(result.error || "Failed to submit preferences.", "error");
		}
	};

	return (
		<Box mt={5}>
			<Typography variant="h5" fontWeight={600} mb={0.5}>
				Agent Preferences
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={3}>
				Select the preferences that apply to you. These options are set by your administrator.
			</Typography>

			<Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
				Changes to your preferences require admin approval before they take effect.
			</Alert>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress size={26} sx={{ color: primaryBlue }} />
				</Box>
			) : availableOptions.length === 0 ? (
				<Paper variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: "center", maxWidth: "575px !important", width: "100% !important" }}>
					<Typography variant="body2" color="text.secondary">
						No preferences have been made available yet.
					</Typography>
				</Paper>
			) : (
				<Paper variant="outlined" sx={{ borderRadius: 2, p: 1, maxWidth: "575px !important", width: "100% !important" }}>
					<Stack divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}>
						{availableOptions.map((option) => {
							const isChecked = checkedIds.includes(option.id);
							return (
								<Box
									key={option.id}
									sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 2, py: 1, px: 1.5 }}
								>
									<Typography variant="body2" fontWeight={600}>
										{option.label}
									</Typography>
									<RadioGroup
										row
										value={isChecked ? "yes" : "no"}
										onChange={(e) => toggleOption(option.id, e.target.value === "yes")}
										sx={{ flexWrap: "nowrap" }}
									>
										<FormControlLabel
											value="yes"
											control={<Radio size="small" sx={{ color: "#B9C7DC", "&.Mui-checked": { color: primaryBlue } }} />}
											label="Yes"
											sx={{ mr: 1, whiteSpace: "nowrap" }}
										/>
										<FormControlLabel
											value="no"
											control={<Radio size="small" sx={{ color: "#B9C7DC", "&.Mui-checked": { color: primaryBlue } }} />}
											label="No"
											sx={{ mr: 0, whiteSpace: "nowrap" }}
										/>
									</RadioGroup>
								</Box>
							);
						})}
					</Stack>
				</Paper>
			)}

			<Typography variant="subtitle1" fontWeight={600} mt={3} mb={1}>
				Remarks
			</Typography>
			<Paper variant="outlined" sx={{ borderRadius: 2, maxWidth: "575px !important", width: "100% !important" }}>
				<RichEditor value={remarks} onChange={setRemarks} placeholder="Add any additional context for these preferences..." />
			</Paper>

			<Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
				<Button
					variant="contained"
					startIcon={<SaveOutlinedIcon />}
					onClick={handleSave}
					disabled={loading || submitting || availableOptions.length === 0}
					sx={saveButtonSx}
				>
					{submitting ? "Submitting..." : "Submit for Approval"}
				</Button>
			</Box>
		</Box>
	);
};

export default AgentPreferenceChecklist;
